import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import * as UserActions from '@store/user/user.actions';
import { SaveEmailPreferenceAction, SaveMapBoundsPreferencesAction, SaveRegionsFailAction } from '@store/user/user.actions';
import * as SearchBoardActions from '@store/search-board/search-board.actions';
import { ChangeGridData, SaveCompactMode, SetSelectedMySearchModal } from '@store/search-board/search-board.actions';
import * as SettingsActions from '@store/settings/settings.actions';
import { GetUserPreferencesSuccess } from '@store/settings/settings.actions';
import { ClearReportsPreferenceGroups } from '@store/reports/reports.actions';
import { catchError, debounceTime, filter, first, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { AppState } from '@root/app-state.module';
import { Action, select, Store } from '@ngrx/store';
import { selectPreferenceElement, selectPreferenceGroupById, selectTemplate } from '@store/user/user.selector';
import { PreferenceService } from '@root/search/services/preference.service';
import { HttpErrorResponse } from '@angular/common/http';
import { RibbonService } from '@shared/services/ribbon.service';
import { RIBBON_TYPE } from '@shared/models/ribbon.model';
import { SpinnerOverlayService } from '@shared/services/spinner-overlay.service';
import { PREFERENCES_SAVE_DEBOUNCE_TIME, SearchField, TemplateCodeResponse } from '@store/search-board/search-board.model';
import {
  ComponentCode,
  DisplayOptions,
  PreferenceCode,
  PreferenceElement,
  PreferenceGroup,
  PreferenceGroupId,
  Template,
  TemplateCode,
  UpdatePreference
} from '@store/user/user.model';
import { ActionName } from '@shared/models/effects-names.model';
import { SetReportsAsNotLoaded, } from '@store/reports/reports.actions';
import { UserPreferences } from '@store/settings/settings.model';
import { PreferenceUtils } from '@shared/utils/preference-utils';
import { MixpanelService } from '@shared/services/mixpanel.service';
import { selectMySearchFieldsData } from '@store/search-board/search-board.selector';
import { google } from '@shared/models/googlemaps.model';
import LatLngLiteral = google.maps.LatLngLiteral;

@Injectable()
export class PreferencesEffects {

  constructor(private readonly actions$: Actions,
              private readonly store$: Store<AppState>,
              private readonly ribbonService: RibbonService,
              private readonly preferenceService: PreferenceService,
              private readonly spinnerOverlayService: SpinnerOverlayService,
              private readonly mixpanelService: MixpanelService) {
  }

  saveRegion$: Observable<Action> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(UserActions.SAVE_REGIONS),
        withLatestFrom(
          this.store$.pipe(select(selectPreferenceGroupById(), PreferenceGroupId.MYREGION_COUNTIES)),
          this.store$.pipe(select(selectPreferenceGroupById(), PreferenceGroupId.COMMON_SEARCH_CONFIG)),
        ),
        this.spinnerOverlayService.wrapRequest(([action, myRegionPreferenceGroup, searchConfigPreferenceGroup]) =>
          this.preferenceService.saveRegions(
            {
              changeRegionRequiredPrefGroups: [myRegionPreferenceGroup, searchConfigPreferenceGroup],
              myRegionSelections: (action as UserActions.SaveRegionsAction).payload
            }
          ).pipe(
            withLatestFrom(
              this.store$.pipe(select(selectMySearchFieldsData))
            ),
            switchMap(([mapBounds, searchField]: [Array<LatLngLiteral>, Array<SearchField>]) => {
              this.ribbonService.openRibbon(RIBBON_TYPE.SUCCESS, 'Counties have been updated.');

              return [
                new UserActions.SaveRegionsSuccessAction({
                  regions: (action as UserActions.SaveRegionsAction).payload,
                  mapBounds
                }),
                new SearchBoardActions.GetLookUpsForNewRegions((action as UserActions.SaveRegionsAction).payload),
                new SearchBoardActions.GetSearchResultsCount(searchField)
              ];
            }),
            catchError(() => {
              return of(new SaveRegionsFailAction());
            })), ActionName.SAVE_REGIONS
        )
      );
  });

  saveRegionFailed$: Observable<Action> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(UserActions.SAVE_REGIONS_FAIL),
        tap(() => {
          this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'Something went wrong. Try to save regions again.');
        })
      );
  }, {dispatch: false});

  saveMySearch$: Observable<Action> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(UserActions.SAVE_CUSTOM_MY_SEARCH),
        this.spinnerOverlayService.wrapRequest((action: UserActions.SaveCustomMySearch) => {
          return this.preferenceService.saveMySearch(action.payload).pipe(
            map((response: TemplateCodeResponse) => {
              return new UserActions.SaveCustomMySearchSuccess({
                ...action.payload,
                templateCode: response.templateCode
              } as Template);
            }),
            tap(() => this.ribbonService.openRibbon(RIBBON_TYPE.SUCCESS, `${action.payload.label} has been created.`)),
            catchError(() => of(null))
          );
        }, ActionName.SAVE_MY_SEARCH)
      );
  });

  saveMySearchFromModal$: Observable<Action> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(SearchBoardActions.SAVE_SELECTED_EDIT_SEARCH_FROM_MODAL),
        this.spinnerOverlayService.wrapRequest((action: SearchBoardActions.SaveSelectedEditSearchFromModal) => {
          return this.preferenceService.saveMySearch(action.payload).pipe(
            map((response: TemplateCodeResponse) => {
              this.mixpanelService.track('Search - Save Search', {
                fieldsInSearch: action.payload.dataElementList.map((x) => x.fieldCode)
              });
              return new SearchBoardActions.SaveCustomMySearchFromModalSuccess({
                ...action.payload,
                templateCode: response.templateCode
              } as Template);
            }),
            tap(() => this.ribbonService.openRibbon(RIBBON_TYPE.SUCCESS, `${action.payload.label} has been created.`)),
            catchError(() => of(null))
          );
        }, ActionName.SAVE_MY_SEARCH_FROM_MODAL)
      );
  });

  removeCustomMySearch$: Observable<Action> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(UserActions.REMOVE_CUSTOM_MY_SEARCH),
        this.spinnerOverlayService.wrapRequest((action: UserActions.RemoveCustomMySearch) => {
          return this.preferenceService.deleteCustomMySearch(action.payload)
            .pipe(
              catchError((error: HttpErrorResponse) => {
                this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'Something went wrong. Try to delete search again.');
                return of(error);
              }),
              filter((error) => !error),
              switchMap(() => {
                return this.store$.pipe(
                  select(selectTemplate(TemplateCode.TMPL_MY_SEARCH_DEFAULT)),
                  first(),
                  map((dt: Template) => {
                    this.ribbonService.openRibbon(RIBBON_TYPE.SUCCESS, 'Search has been deleted.');
                    this.mixpanelService.track('Search - Delete Search');
                    return new UserActions.RemoveCustomMySearchSuccess({removedTemplateCode: action.payload, defaultTemplate: dt});
                  })
                );
              })
            );
        }, ActionName.REMOVE_MY_SEARCH)
      );
  });

  updateCustomMySearch$: Observable<Action> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(UserActions.UPDATE_CUSTOM_MY_SEARCH),
        this.spinnerOverlayService.wrapRequest((action: UserActions.UpdateCustomMySearch) => {
          return this.preferenceService.updateCustomMySearch(action.payload)
            .pipe(
              catchError((error: HttpErrorResponse) => {
                this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'Something went wrong. Try to save search again.');
                return of(error);
              }),
              filter((error) => !error),
              switchMap(() => {
                this.ribbonService.openRibbon(RIBBON_TYPE.SUCCESS, 'Search has been updated.');
                return [
                  new SetSelectedMySearchModal(action.payload),
                  new UserActions.UpdateCustomMySearchSuccess(action.payload)
                ];
              }),
            );
        }, ActionName.UPDATE_MY_SEARCH)
      );
  });

  saveEmailSignature$: Observable<Action> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(UserActions.SAVE_EMAIL_SIGNATURE),
        this.spinnerOverlayService.wrapRequest((action: UserActions.SaveEmailSignature) => {
          return this.preferenceService.saveEmailSignature(action.payload)
            .pipe(
              catchError((error: HttpErrorResponse) => {
                this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'Something went wrong. Try to save signature again.');
                return of(error);
              }),
              filter((error) => !error),
              map(() => {
                this.ribbonService.openRibbon(RIBBON_TYPE.SUCCESS, 'Signature has been saved.');
                return new UserActions.SaveEmailSignatureSuccess(action.payload);
              })
            );
        }, ActionName.SAVE_EMAIL_SIGNATURE)
      );
  });

  setSelectedSearch: Observable<void> = createEffect(() => {
    return this.actions$.pipe(
      ofType(SearchBoardActions.SET_MY_SEARCH_LAST_LOADED_TEMPLATE),
      withLatestFrom(this.store$.pipe(select(selectPreferenceElement(
        PreferenceCode.PG_MY_SEARCH_CONFIG,
        PreferenceCode.PREF_MY_SEARCH_LAST_LOADED_TEMPLATE
      )))),
      switchMap(([action, prefElement]: [SearchBoardActions.SetMySearchLastLoadedTemplate, PreferenceElement]) => {
        const newPrefElement: PreferenceElement = {
          ...prefElement,
          preferenceValue: {
            ...prefElement.preferenceValue,
            values: [action.payload.templateCode]
          }
        };

        return this.preferenceService.savePreferenceGroupSettings([{
          preferenceElementsList: [newPrefElement],
          componentCode: ComponentCode.COMP_MY_SEARCH,
          prefGroupCode: PreferenceCode.PG_MY_SEARCH_CONFIG
        }]);
      })
    );
  }, {dispatch: false});

  setLastLoadedExportTemplate: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(SearchBoardActions.SET_LAST_LOADED_EXPORT_TEMPLATE),
      withLatestFrom(this.store$.pipe(select(selectPreferenceElement(
        PreferenceCode.PG_EXPORT_CONFIG,
        PreferenceCode.PREF_EXPORT_LAST_LOADED_TEMPLATE
      )))),
      switchMap(([action, prefElement]: [SearchBoardActions.SetLastLoadedExportTemplate, PreferenceElement]) => {
        const newPrefElement: PreferenceElement = {
          ...prefElement,
          preferenceValue: {
            ...prefElement.preferenceValue,
            values: [action.payload.templateCode]
          }
        };
        const updatedPref = [{
          preferenceElementsList: [newPrefElement],
          prefGroupCode: PreferenceCode.PG_EXPORT_CONFIG
        }];

        return this.preferenceService.savePreferenceGroupSettings(updatedPref).pipe(
          filter(Boolean),
          map(() => new UserActions.SavePreferenceGroupSuccess(updatedPref))
        );
      })
    );
  });

  savePreferenceGroup$: Observable<Action> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(UserActions.SAVE_PREFERENCE_GROUP),
        this.spinnerOverlayService.wrapRequest((action: UserActions.SavePreferenceGroup) =>
          this.savePreferenceGroup(action), ActionName.SAVE_PREFERENCE_GROUP),
        map((action: UserActions.SaveReportsPreferenceGroup) => new UserActions.SavePreferenceGroupSuccess(action.payload))
      );
  });

  savePreferenceGroupSilent$: Observable<UserActions.SavePreferenceGroupSuccess> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(UserActions.SAVE_PREFERENCE_GROUP_SILENT),
        mergeMap((action: UserActions.SavePreferenceGroupSilent) => {
          return this.preferenceService.savePreferenceGroupSettings(action.payload)
            .pipe(
              catchError(() => of(null)),
              filter(Boolean),
              map(() => new UserActions.SavePreferenceGroupSuccess(action.payload))
            );
        })
      );
  });

  saveReportsPreferenceGroup$: Observable<Action> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(UserActions.SAVE_REPORTS_PREFERENCE_GROUP),
        this.spinnerOverlayService.wrapRequest((action: UserActions.SaveReportsPreferenceGroup) =>
          this.savePreferenceGroup(action), ActionName.SAVE_REPORTS_PREFERENCE_GROUP),
        map((action: UserActions.SaveReportsPreferenceGroup) => new UserActions.SaveReportsPreferenceGroupSuccess(action.payload))
      );
  });

  saveReportsPreferenceGroupSuccess$: Observable<Action> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(UserActions.SAVE_REPORTS_PREFERENCE_GROUP_SUCCESS),
        map(() => new SetReportsAsNotLoaded())
      );
  });

  restoreDefaultPreference$: Observable<Action> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(SettingsActions.RESTORE_DEFAULT_PREFERENCE),
        this.spinnerOverlayService.wrapRequest((_: SettingsActions.RestoreDefaultPreference) => {
          return this.preferenceService.restoreDefaultPreference()
            .pipe(
              catchError(() => {
                this.ribbonService.openRibbon(RIBBON_TYPE.ERROR,
                  'Preference cannot be restored to default right now. Please try again later.');
                return of(null);
              }),
              filter(Boolean),
              tap(() => this.ribbonService.openRibbon(RIBBON_TYPE.SUCCESS, 'Your Preference has been restored to default.')),
              mergeMap((result: Array<UserPreferences>) => {
                return [new GetUserPreferencesSuccess(result), new ClearReportsPreferenceGroups()];
              })
            );
        }, ActionName.RESTORE_DEFAULT_PREFERENCE)
      );
  });

  saveChangesUserPreference$: Observable<Action> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(SettingsActions.SAVE_CHANGES_USER_PREFERENCE),
        this.spinnerOverlayService.wrapRequest(
          (action: SettingsActions.SaveChangesUserPreference) => {
            const updatePreference: Array<UpdatePreference> = action.payload.preferenceGroups.map(
              (prefGroup: PreferenceGroup) => {
                return {
                  preferenceElementsList: prefGroup.preferenceElements,
                  prefGroupCode: prefGroup.prefGroupCode as PreferenceCode
                };
              });
            return this.preferenceService.savePreferenceGroupSettings(updatePreference)
              .pipe(
                catchError(() => {
                  this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'Preference cannot be updated right now. Please try again later.');
                  return of(null);
                }),
                filter(Boolean),
                tap(() => this.ribbonService.openRibbon(RIBBON_TYPE.SUCCESS, 'Your Preference has been updated.')),
                map(() => new UserActions.SavePreferenceGroupSuccess(updatePreference))
              );
          }, ActionName.SAVE_CHANGES_USER_PREFERENCE)
      );
  });

  getUserPreferences$: Observable<Action> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(SettingsActions.GET_USER_PREFERENCES),
        this.spinnerOverlayService.wrapRequest(() => {
          return this.preferenceService.getUserPreferences()
            .pipe(
              catchError(() => {
                this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'Preference cannot be loaded right now. Please try again later.');
                return of(null);
              }),
              filter(Boolean),
              mergeMap((result: Array<UserPreferences>) => {
                return [new GetUserPreferencesSuccess(result), new ClearReportsPreferenceGroups()];
              }));
        }, ActionName.GET_USER_PREFERENCES)
      );
  });

  saveMapBoundsPreferences$: Observable<void> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(UserActions.SAVE_MAP_BOUNDS_PREFERENCES),
        debounceTime(PREFERENCES_SAVE_DEBOUNCE_TIME),
        withLatestFrom(this.store$.pipe(
          select(selectPreferenceElement(PreferenceCode.PG_RL_GENERAL_PREFERENCES, PreferenceCode.PG_GENPREF_MAP_BOUNDS)))),
        switchMap(([action, preferenceElement]: [SaveMapBoundsPreferencesAction, PreferenceElement]) => {
          return this.preferenceService.savePreferenceGroupSettings([{
            prefGroupCode: PreferenceCode.PG_RL_GENERAL_PREFERENCES,
            preferenceElementsList: [{
              ...preferenceElement,
              preferenceValue: {
                values: action.payload.map((location: LatLngLiteral) => JSON.stringify(location))
              }
            }]
          }])
            .pipe(catchError(() => of(null)));
        })
      );
  }, {dispatch: false});

  saveGridMapRatio$: Observable<UserActions.SavePreferenceGroupSilent> = createEffect(() => {
    return this.actions$.pipe(
      ofType(SearchBoardActions.CHANGE_GRID_DATA),
      debounceTime(PREFERENCES_SAVE_DEBOUNCE_TIME),
      withLatestFrom(this.store$.pipe(
        select(selectPreferenceElement(PreferenceCode.PG_MAP_CONFIG, PreferenceCode.PREF_MAP_MAP_TO_GRID_RATIO)))
      ),
      map(([action, preferenceElement]: [ChangeGridData, PreferenceElement]) => {
        return new UserActions.SavePreferenceGroupSilent([{
          prefGroupCode: PreferenceCode.PG_MAP_CONFIG,
          preferenceElementsList: [
            PreferenceUtils.setPreferenceValue(preferenceElement, action.payload.map)
          ]
        }]);
      })
    );
  });

  saveGridCompactMode$: Observable<UserActions.SavePreferenceGroupSilent> = createEffect(() => {
    return this.actions$.pipe(
      ofType(SearchBoardActions.SAVE_COMPACT_MODE),
      debounceTime(PREFERENCES_SAVE_DEBOUNCE_TIME),
      withLatestFrom(this.store$.pipe(
        select(selectPreferenceElement(PreferenceCode.PG_PROPERTY_GRID_CONFIG, PreferenceCode.PREF_RL_PROP_GRID_COMPACT_MODE))
      )),
      map(([action, preferenceElement]: [SaveCompactMode, PreferenceElement]) => {
        return new UserActions.SavePreferenceGroupSilent([{
          prefGroupCode: PreferenceCode.PG_PROPERTY_GRID_CONFIG,
          preferenceElementsList: [
            PreferenceUtils.setPreferenceValue(preferenceElement, action.payload ? DisplayOptions.YES : '')
          ]
        }]);
      })
    );
  });

  saveEmailPreference$: Observable<void> = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.SAVE_EMAIL_PREFERENCE_ACTION),
      withLatestFrom(this.store$.pipe(
        select(selectPreferenceElement(PreferenceCode.PG_RL_GENERAL_PREFERENCES, PreferenceCode.PG_GENPREF_USER_EMAIL)))),
      switchMap(([action, preferenceElement]: [SaveEmailPreferenceAction, PreferenceElement]) => {
        return this.preferenceService.savePreferenceGroupSettings([{
          prefGroupCode: PreferenceCode.PG_RL_GENERAL_PREFERENCES,
          preferenceElementsList: [
            PreferenceUtils.setPreferenceValue(preferenceElement, action.payload)
          ]
        }]);
      })
    );
  }, {dispatch: false});

  private savePreferenceGroup(action: UserActions.SavePreferenceGroup | UserActions.SaveReportsPreferenceGroup) {
    return this.preferenceService.savePreferenceGroupSettings(action.payload)
      .pipe(
        catchError(() => {
          this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'Something went wrong. Try to save preference again.');
          return of(null);
        }),
        filter(Boolean),
        tap(() => this.ribbonService.openRibbon(RIBBON_TYPE.SUCCESS, 'Preference has been saved.')),
        map(() => action)
      );
  }
}
